import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../lib/api.js'
import ContextMenu from './ContextMenu.jsx'
import { startDragGuard, dragEnded } from '../lib/dragGuard.js'

const COLORS = ['#ffff00', '#39ff14', '#ff6eff', '#ff5f1f', '#bf6eff']

export default function ReminderPostits() {
  const [reminders, setReminders] = useState([])
  const [postits, setPostits] = useState([])
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_reminders') || '[]') } catch { return [] }
  })
  const [reminderPositions, setReminderPositions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('reminder_positions') || '{}') } catch { return {} }
  })
  const [remindersEnabled, setRemindersEnabled] = useState(() => {
    return localStorage.getItem('due_reminders_enabled') !== 'false'
  })
  const [contextMenu, setContextMenu] = useState(null)

  useEffect(() => {
    loadReminders()
    loadPostits()
    const interval = setInterval(loadReminders, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Auf Aenderung des Schalters in den Einstellungen reagieren (gleicher Tab)
  useEffect(() => {
    function onPrefChange() {
      setRemindersEnabled(localStorage.getItem('due_reminders_enabled') !== 'false')
    }
    window.addEventListener('due-reminders-pref-changed', onPrefChange)
    return () => window.removeEventListener('due-reminders-pref-changed', onPrefChange)
  }, [])

  async function loadReminders() {
    try {
      const today = new Date().toISOString().slice(0, 10)
      const tasks = await api.tasks({ status: 'open' })
      const due = (tasks || []).filter(t => {
        if (!t.due_date) return false
        const d = normalizeDateStr(t.due_date)
        return d && d <= today
      })
      setReminders(due)
    } catch { /* silent */ }
  }

  async function loadPostits() {
    try {
      const data = await api.postits()
      setPostits(data || [])
    } catch { /* silent */ }
  }

  function normalizeDateStr(str) {
    if (!str) return null
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10)
    const m = str.match(/^(\d{2})\.(\d{2})\.(\d{2,4})/)
    if (m) {
      const year = m[3].length === 2 ? '20' + m[3] : m[3]
      return `${year}-${m[2]}-${m[1]}`
    }
    return null
  }

  function dismiss(taskId) {
    const updated = [...dismissed, taskId]
    setDismissed(updated)
    localStorage.setItem('dismissed_reminders', JSON.stringify(updated))
  }

  function saveReminderPos(taskId, pos) {
    setReminderPositions(prev => {
      const updated = { ...prev, [taskId]: pos }
      localStorage.setItem('reminder_positions', JSON.stringify(updated))
      return updated
    })
  }

  async function createPostitColor(x, y, color) {
    try {
      const data = await api.createPostit({ text: '', color, x, y })
      setPostits(prev => [...prev, data])
    } catch (err) {
      console.error('[Postits]', err)
    }
  }

  async function updatePostit(id, updates) {
    try {
      const updated = await api.updatePostit(id, updates)
      setPostits(prev => prev.map(p => p.id === id ? updated : p))
    } catch (err) {
      console.error('[Postits]', err)
    }
  }

  async function deletePostit(id) {
    try {
      await api.deletePostit(id)
      setPostits(prev => prev.filter(p => p.id !== id))
    } catch (err) {
      console.error('[Postits]', err)
    }
  }

  // Global right-click handler for creating post-its
  useEffect(() => {
    function handleContextMenu(e) {
      // Only on empty areas (not on interactive elements)
      const tag = e.target.tagName.toLowerCase()
      const isInteractive = ['a', 'button', 'input', 'textarea', 'select'].includes(tag)
      const inCard = e.target.closest('.card, .list-item, .modal-overlay, .agent-widget-panel, .sidebar, [data-no-postit]')
      if (isInteractive || inCard) return

      e.preventDefault()
      setContextMenu({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('contextmenu', handleContextMenu)
    return () => window.removeEventListener('contextmenu', handleContextMenu)
  }, [])

  const visibleReminders = remindersEnabled
    ? reminders.filter(r => !dismissed.includes(r.id))
    : []

  return (
    <>
      {/* Task Reminders — centered at top */}
      {visibleReminders.length > 0 && (
        <div style={{
          position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999,
          display: 'flex', flexDirection: 'column', gap: 10,
          pointerEvents: 'none'
        }}>
          {visibleReminders.map((task, i) => (
            <PostItCard
              key={`task-${task.id}`}
              bg={COLORS[task.id % COLORS.length]}
              initialPos={reminderPositions[task.id] || { x: 0, y: i * 10 }}
              onClose={() => dismiss(task.id)}
              onPosChange={(pos) => saveReminderPos(task.id, pos)}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6, paddingRight: 16 }}>
                {task.title}
              </div>
              {task.description && task.description !== task.title && (
                <div style={{ fontSize: 11, color: '#666', marginBottom: 8, lineHeight: 1.4 }}>
                  {task.description.length > 120 ? task.description.slice(0, 120) + '...' : task.description}
                </div>
              )}
              <div style={{ fontSize: 10, color: '#555' }}>
                {task.due_date ? `Fällig: ${task.due_date}` : ''}
                {task.project_name ? ` · ${task.project_name}` : ''}
              </div>
            </PostItCard>
          ))}
        </div>
      )}

      {/* Custom Post-its — absolute positioned */}
      {postits.map(p => (
        <CustomPostIt
          key={p.id}
          postit={p}
          onUpdate={(updates) => updatePostit(p.id, updates)}
          onDelete={() => deletePostit(p.id)}
        />
      ))}

      {/* Context Menu for creating new post-it with color choice */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            {
              label: 'Neuer Post-it',
              onClick: () => createPostitColor(contextMenu.x, contextMenu.y, COLORS[0]),
            },
            {
              type: 'colors',
              colors: COLORS.map(c => ({ label: c, value: c })),
              onSelect: (c) => createPostitColor(contextMenu.x, contextMenu.y, c),
            },
          ]}
        />
      )}
    </>
  )
}

/** Draggable card shell used by both reminder and custom post-its */
function PostItCard({ bg, initialPos, onClose, onPosChange, children, style }) {
  const [pos, setPos] = useState(initialPos)
  const [dragging, setDragging] = useState(false)
  const dragStart = useRef(null)
  const didDrag = useRef(false)

  const onMouseDown = useCallback((e) => {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'TEXTAREA') return
    setDragging(true)
    didDrag.current = false
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
  }, [pos])

  useEffect(() => {
    if (!dragging) return
    function onMove(e) {
      if (dragEnded(e)) { onUp(e); return }
      didDrag.current = true
      setPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y })
    }
    function onUp(e) {
      setDragging(false)
      if (didDrag.current) {
        const finalPos = { x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y }
        onPosChange?.(finalPos)
      }
    }
    const endGuard = startDragGuard()
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { endGuard(); window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [dragging, onPosChange])

  const onTouchStart = useCallback((e) => {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'TEXTAREA') return
    const touch = e.touches[0]
    setDragging(true)
    didDrag.current = false
    dragStart.current = { x: touch.clientX - pos.x, y: touch.clientY - pos.y }
  }, [pos])

  useEffect(() => {
    if (!dragging) return
    let lastPos = null
    function onMove(e) {
      const touch = e.touches[0]
      if (!touch) return
      didDrag.current = true
      lastPos = { x: touch.clientX - dragStart.current.x, y: touch.clientY - dragStart.current.y }
      setPos(lastPos)
    }
    function onUp() {
      setDragging(false)
      if (didDrag.current && lastPos) onPosChange?.(lastPos)
    }
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('touchend', onUp)
    return () => { window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp) }
  }, [dragging, onPosChange])

  return (
    <div
      data-no-postit
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      style={{
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        background: bg,
        padding: '14px 16px 10px',
        borderRadius: 4,
        boxShadow: '2px 3px 12px rgba(0,0,0,0.18)',
        width: 220,
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        pointerEvents: 'auto',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        position: 'relative',
        transition: dragging ? 'none' : 'transform 0.2s ease',
        ...style
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 4, right: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 16, color: '#333', lineHeight: 1, padding: '2px 4px'
        }}
        title="Entfernen"
      >&times;</button>
      {children}
    </div>
  )
}

/** Custom user-created post-it with editable text and color picker */
function CustomPostIt({ postit, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(!postit.text)
  const [text, setText] = useState(postit.text)
  const textRef = useRef(null)
  const saveTimeout = useRef(null)

  useEffect(() => {
    if (editing && textRef.current) textRef.current.focus()
  }, [editing])

  function handleTextChange(val) {
    setText(val)
    clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(() => onUpdate({ text: val }), 500)
  }

  function handlePosUpdate(pos) {
    onUpdate({ x: pos.x, y: pos.y })
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 9998,
      pointerEvents: 'none',
    }}>
      <PostItCard
        bg={postit.color}
        initialPos={{ x: postit.x, y: postit.y }}
        onClose={onDelete}
        onPosChange={handlePosUpdate}
        style={{ pointerEvents: 'auto' }}
      >
        {/* Text area */}
        {editing ? (
          <textarea
            ref={textRef}
            value={text}
            onChange={e => handleTextChange(e.target.value)}
            onBlur={() => { if (text.trim()) setEditing(false) }}
            placeholder="Notiz..."
            style={{
              width: '100%', minHeight: 48, resize: 'vertical',
              background: 'transparent', border: 'none', outline: 'none',
              fontSize: 13, fontWeight: 500, color: '#333', lineHeight: 1.4,
              fontFamily: 'inherit', paddingRight: 16, marginBottom: 16,
            }}
          />
        ) : (
          <div
            onClick={() => setEditing(true)}
            style={{
              fontSize: 13, fontWeight: 500, color: '#333', lineHeight: 1.4,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', cursor: 'text',
              minHeight: 24, paddingRight: 16, marginBottom: 16,
            }}
          >
            {text || 'Klicken zum Bearbeiten...'}
          </div>
        )}
      </PostItCard>
    </div>
  )
}
